import { Button, Input, InputRef, Space, Table } from "antd"
import { useState, useRef, useEffect, useMemo } from "react"
import styles from "./styles.module.less";
import classNames from "classnames"
import { sum } from 'lodash-es'
import { numberFixTo, toDefaultUnitVal, toUnitValWithFormat, toUnitValWithNoCutoff, variableLabel } from "../FlowSheetEditor/components/RightPanel/UnitPanel/components/common/utils"
import { useGetVariableUnit } from "@/pages/Project/hooks/useDimUnits"
import { getStatus, RunState } from "@/store/flowsheet/flowsheet.slice"
import { useSelector } from "react-redux"
import { getProjectPropertySets } from "@/store/project/selector"

interface EditableCellProps {
  record: any;
  handleSave?: (record: any) => void;
}

interface CompositionEditorProps {
  readonly?: boolean
  value?: number[]
  setName?: string
  onChange?: (val: number[]) => void
  isManual?: boolean
  
  onSave?: () => void
  onCancel?: () => void
}

const defCutoff = 10

export  const CompositionEditor = (props: CompositionEditorProps) => {
  const {readonly, value: initVal = [], onChange, setName, isManual = false, onSave, onCancel} = props;
  const [value, setValue] = useState(initVal)
  const propertySets = useSelector(getProjectPropertySets)
  const status = useSelector(getStatus);
  const getUnitEntityByCode = useGetVariableUnit()

  const unitEntiry = useMemo(() => {
    return getUnitEntityByCode('MOL_FRAC')
  }, [getUnitEntityByCode])
  useEffect(() => {
    setValue(initVal)
  }, [initVal])
  const data = useMemo(() => {
    if (!(setName && propertySets && value)) {
      return null
    }
    const propertySet = propertySets.find(item => item.name === setName)
    if (!propertySet) {
      return null
    }
    const dataList = propertySet.components.map((item, index) => {
      return {
        name: item.name,
        fraction: unitEntiry ? toUnitValWithNoCutoff(value[index] ?? 0, unitEntiry) : value[index] ?? 0,
        label: unitEntiry ? toUnitValWithFormat(value[index] ?? 0, unitEntiry) : numberFixTo(value[index] ?? 0, defCutoff) || 0
      }
    })
    if (readonly) {
      return dataList.filter(item => item.fraction !== 0)
    }
    return dataList
  }, [propertySets, setName, value, readonly, unitEntiry])

  const onRowValueChange = (newRow, index) => {
    const val = data?.map(item => Number(item.fraction) || 0) || []
    val[index] = Number(newRow.fraction) || 0;
    const valWithUnit = val.map(itemVal => {
      return toDefaultUnitVal(Number(itemVal), unitEntiry?.unit) || 0;
    })
    setValue(valWithUnit)
    onChange?.(valWithUnit)
  }

  const formatShowText = (record) => {
    if (record.label === 0 && record.label !== record.fraction) {
      return `≈ 0`
    }
    return record.label
  }

  if (!data) {
    return <div>
      Please selected a propertyset.
    </div>
  }

  if (data.length === 0) {
    return <div>
      Please change the composition.
    </div>
  }

  
  const totalFraction = numberFixTo(sum(data.map(item => Number(item.fraction || '0'))), defCutoff)

  const totalShouldBe = unitEntiry ? toUnitValWithFormat(1, unitEntiry) : 1
  const not100 = totalFraction !== totalShouldBe;

  const columns = [
    {
      title: 'Component',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: variableLabel('Mole Fraction', unitEntiry),
      dataIndex: 'fraction',
      key: 'fraction',
      render: (value, record, index) => {
        if (readonly) return <span
          className={classNames((isManual && status !== RunState.Stop) && 'manual-value')}>
            {formatShowText(record)}
          </span>
        return <EditableCell record={record} handleSave={(newRow) => onRowValueChange(newRow, index)}>{formatShowText(record)}</EditableCell>
      }
    },
  ]

  const editorStyles = !readonly ? {maxHeight: 500, overflowY: 'scroll'} : undefined

  return <div className={isManual ? 'show-manual-color' : ''}>
    <div style={editorStyles as any}>
      <Table bordered dataSource={data} columns={columns} size="small" pagination={false} />
    </div>
    {!readonly && 
      <>
        <div className="mt-2 flex-end">
          <Space>
            <Button type="default">Normalize</Button>
            <div><span>Total:</span> <div className={classNames(styles.totalFraction, not100 && styles.errorStatus)}>
              {not100 ? `${totalFraction}, Expect: ${totalShouldBe}` : totalFraction}
            </div></div>
          </Space>
        </div>

        <div className="mt-2 flex-end">
          <Space>
            <Button type="default" onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={onSave} disabled={not100}>Save</Button>
          </Space>
        </div>
      </>
    }

  </div>
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  children,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const [value, setValue] = useState(record.fraction)

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
  };

  const save = async () => {
    toggleEdit();
    handleSave?.({...record, fraction: value})
  };

  let childNode = children;

  childNode = (
    <div
      className="editable-cell-value-wrap"
      onClick={toggleEdit}
    >
      {editing && <Input
        ref={inputRef}
        onPressEnter={save}
        onBlur={save}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        style={{width: '100%', height: 22}}
      />}
      {!editing && children}
    </div>
  );

  return <td {...restProps} className="manual-value">{childNode}</td>;
};